import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setPage } from '../../store/slices/pageSlice';
import { setBonusPosition, resetForm } from '../../store/slices/promoFormSlice';
import feeApi from '../../api/feeApi';
import PositionsApi from '../../api/PositionsApi';
import CrystalsApi from '../../api/CrystalsApi';
import useCountdown, { resolveFreeTrialEndsAt } from '../../hooks/useCountdown';

const IMAGE_BASE_URL = 'https://api.duotetra.quest/uploads/gems/';

/**
 * Баннер «1st Time Free» — компактная карточка с иконкой кристалла.
 * При клике переходим на создание бонусного промо.
 */
export default function BonusBannerCard({ position, expiresAt, onPress, onExpired }) {
    const dispatch = useDispatch();
    const [fee, setFee] = useState(null);
    const [positionData, setPositionData] = useState(null);
    const [gemData, setGemData] = useState(null);

    const endsAt = useMemo(() => resolveFreeTrialEndsAt(expiresAt), [expiresAt]);
    const countdown = useCountdown(endsAt);

    useEffect(() => {
        if (countdown.expired) onExpired?.();
    }, [countdown.expired, onExpired]);

    // Загружаем данные позиции по categoryId
    useEffect(() => {
        if (!position?.categoryId) return;

        PositionsApi.getPositionSettingById(position.categoryId)
            .then((data) => {
                setPositionData(data);
                if (data?.gemId) {
                    CrystalsApi.getGemById(data.gemId)
                        .then(setGemData)
                        .catch(console.error);
                }
            })
            .catch(console.error);
    }, [position?.categoryId]);

    const gemImage = gemData?.frontImage ? `${IMAGE_BASE_URL}${gemData.frontImage}` : null;
    const categoryName = positionData?.name || position?.name;
    const promoType = positionData?.promoType || 'onlyCrystals';

    const promoTypeLabels = {
        onlyCrystals: 'Crystals',
        crystalsAndCrypto: 'Crystals & Crypto',
        cryptoOnly: 'Crypto',
    };

    useEffect(() => {
        const participants = position?.maxParticipants || 50;
        feeApi.calculateGemFeePreview(participants)
            .then((calculatedFee) => {
                setFee(calculatedFee);
            })
            .catch((err) => {
                console.error('[BonusBannerCard] calculateGemFeePreview error:', err);
                setFee(null);
            });
    }, [position?.maxParticipants]);

    const isDataLoaded = !!positionData && !!positionData.promoType;

    const handleClick = () => {
        if (!isDataLoaded) {
            console.warn('[BonusBannerCard] Data not loaded yet, waiting...');
            return;
        }

        dispatch(resetForm());

        const enrichedPosition = {
            ...position,
            gemId: positionData?.gemId,
            promoType: positionData?.promoType,
            positionName: positionData?.name,
            minParticipantsCount: positionData?.minParticipantsCount,
            minPrizePlacesCount: positionData?.minPrizePlacesCount,
            gemName: gemData?.name,
            gemFrontImage: gemData?.frontImage,
        };

        console.log('[BonusBannerCard] Setting bonus position:', enrichedPosition);
        dispatch(setBonusPosition(enrichedPosition));
        dispatch(setPage({ id: 1, name: 'PromoFormCrystals' }));
        onPress?.();
    };

    if (countdown.expired) return null;

    return (
        <div
            className={`w-full rounded-[10px] overflow-hidden cursor-pointer active:scale-[0.98] transition-transform ${!isDataLoaded ? 'opacity-70' : ''}`}
            onClick={handleClick}
            style={{
                background: 'linear-gradient(90deg, #DC1FFF 0%, #3A88F7 100%)',
            }}
        >
            <div className="w-full px-4 py-3 flex flex-row items-center gap-3">
                <div className="w-[50px] h-[50px] rounded-full bg-white/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {gemImage ? (
                        <img
                            src={gemImage}
                            alt=""
                            className="w-[38px] h-[38px] object-contain"
                        />
                    ) : (
                        <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                        </svg>
                    )}
                </div>

                <div className="flex-1 flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            className="text-white text-[18px] leading-tight"
                            style={{ fontFamily: 'Anton, sans-serif' }}
                        >
                            1st Time Free
                        </span>
                        {fee != null && (
                            <span className="text-white/70 text-[14px] line-through font-medium">
                                ${Number(fee).toFixed(2)}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-1.5">
                        <span className="text-white/70 text-[10px] font-medium uppercase tracking-wide">
                            Ends in
                        </span>
                        <div className="flex items-center gap-1 text-white text-[12px] font-semibold tabular-nums">
                            <span className="px-1.5 py-0.5 rounded bg-black/25">{countdown.days}d</span>
                            <span className="opacity-60">:</span>
                            <span className="px-1.5 py-0.5 rounded bg-black/25">
                                {String(countdown.hours).padStart(2, '0')}h
                            </span>
                            <span className="opacity-60">:</span>
                            <span className="px-1.5 py-0.5 rounded bg-black/25">
                                {String(countdown.minutes).padStart(2, '0')}m
                            </span>
                            <span className="opacity-60">:</span>
                            <span className="px-1.5 py-0.5 rounded bg-black/25">
                                {String(countdown.seconds).padStart(2, '0')}s
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/20">
                            {gemImage && (
                                <img src={gemImage} alt="" className="w-4 h-4 object-contain" />
                            )}
                            <span className="text-white text-[11px] font-medium">
                                {categoryName || gemData?.name || 'Crystal'}
                            </span>
                        </div>

                        <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/80 text-[10px] font-medium">
                            {promoTypeLabels[promoType] || promoType}
                        </span>
                    </div>
                </div>

                <div className="w-[36px] h-[36px] rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                </div>
            </div>
        </div>
    );
}
